import Markdown from 'react-markdown';
import type { Components } from 'react-markdown';
import type { ReactNode } from 'react';
import remarkGfm from 'remark-gfm';
import { Link, useLocation } from 'react-router-dom';
import { appAccountLink, safeLinkHref, splitScoutReply } from '../lib/scoutReply.ts';

// Render model prose as React elements. Raw HTML is ignored. Two kinds of link survive: an
// account link (`appAccountLink`), which opens that account on My Accounts in the app, and an
// absolute http(s) URL (`safeLinkHref`), which opens in a new tab. Anything else renders as its
// text. Images remain descriptive text rather than remote fetches.
const components: Components = {
  a: ({ children, href }) => <ReplyLink href={href}>{children}</ReplyLink>,
  img: ({ alt }) => <span>{alt || 'Image'}</span>,
  h1: ({ children }) => <h3>{children}</h3>,
  h2: ({ children }) => <h3>{children}</h3>,
  h3: ({ children }) => <h4>{children}</h4>,
  table: ({ children }) => <div className="chatresponse-table" tabIndex={0} role="region" aria-label="Scrollable response table"><table>{children}</table></div>,
};

// An account link is an in-app path, the only href here that starts with `/`: the board, for every
// role. Inside the extension's frame the board has no room, so there it opens in a new tab instead.
function ReplyLink({ href, children }: { href: string | undefined; children: ReactNode }) {
  const { pathname } = useLocation();
  if (!href) return <span>{children}</span>;
  if (href.startsWith('/') && !pathname.startsWith('/embed/')) {
    return <Link to={href}>{children}</Link>;
  }
  return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
}

// react-markdown's URL hook: null drops the attribute, so the `a` above renders a span.
function linkUrl(url: string): string | null {
  return appAccountLink(url, window.location.origin) ?? safeLinkHref(url);
}

export function ChatResponse({ text, streaming = false }: { text: string; streaming?: boolean }) {
  // The follow-up marker is the panel's to render as chips, never prose.
  const { body } = splitScoutReply(text, streaming);
  return (
    <div className="chatresponse">
      <Markdown remarkPlugins={[remarkGfm]} components={components} urlTransform={linkUrl} skipHtml>{body}</Markdown>
    </div>
  );
}
